import React, { Fragment } from "react";
import { Component } from "react";
import { Form, Button } from "semantic-ui-react";
import { ReactCookieProps, withCookies } from "react-cookie";
import base64 from "base-64";
import utf8 from "utf8";
import PhoneNumber from "awesome-phonenumber";
import * as Sentry from "@sentry/nextjs";
import _ from "lodash";
import { ObjectLiteral, parentDataRequiredToRegister } from "../../constants";
import { Props } from "../propTypes";
import { childFormComponent } from "./types";
import { ConfirmationType, ConfirmationTypeData } from "../../store/main/types";
import { ResetForm } from "./ResetForm";

const codeLength = 6;

// Code Form Component
/**
 * This component is used to handle the 6-digit code sent to and entered by the user to verify their identity.
 *
 * For phone numbers, we are using the PhoneNumber component to validate
 * (https://www.npmjs.com/package/awesome-phonenumber)
 *
 * Utf8 and base64 for text-encoding. More information about these can be found at
 * https://www.npmjs.com/package/base-64/ and https://www.npmjs.com/package/utf8/
 *
 * moment.js is used to format the selected start date. For more information, check the documentation
 * at https://momentjs.com/docs/
 *
 * React Cookie is being used to set a cookie on the user's browser for 30 minutes. More information about this
 * can be found at https://www.npmjs.com/package/react-cookie/
 */
type n = "1" | "2" | "3" | "4" | "5" | "6";
type key = `code${n}`;

type CodeType<T> = { [codeNumb in key]: T };

interface State extends CodeType<string> {
    valid: boolean;
    showValidation: boolean;
    validationText: string;
}

class Code extends Component<
    Props & childFormComponent & ReactCookieProps,
    State
> {
    codeRefs: ObjectLiteral = {
        code1refInput: React.createRef<HTMLInputElement>(),
        code2refInput: React.createRef<HTMLInputElement>(),
        code3refInput: React.createRef<HTMLInputElement>(),
        code4refInput: React.createRef<HTMLInputElement>(),
        code5refInput: React.createRef<HTMLInputElement>(),
        code6refInput: React.createRef<HTMLInputElement>(),
    };

    state: State = {
        code1: "",
        code2: "",
        code3: "",
        code4: "",
        code5: "",
        code6: "",
        valid: false,
        showValidation: false,
        validationText: "",
    };

    // Component Did Update Lifecycle Method
    /**
     * componentDidUpdate() runs immediately after the component updates.
     * This method does not run during the initial render.
     *
     * In this instance, we are validating the code entered by the user. If there is an error, we return a message.
     * If valid, we create a cookie for 30 minutes, so that if the user goes to the form again, they can skip this step.
     */
    componentDidUpdate(
        prevProps: Props & childFormComponent & ReactCookieProps
    ) {
        if (
            this.props.main.validateConfirmationCodeData &&
            this.props.main.validateConfirmationCodeData.status_code &&
            this.props.main.validateConfirmationCodeData.status_code !== 200 &&
            this.props.main.validateConfirmationCodeData.status_code !== 409 &&
            this.props.main.validateConfirmationCodeData.status_code !== 404 &&
            prevProps.main.validateConfirmationCodeData &&
            prevProps.main.validateConfirmationCodeData.status_code !==
                this.props.main.validateConfirmationCodeData.status_code
        ) {
            let errorText = "Error confirming code. Please try again later.";

            if (
                this.props.main.validateConfirmationCodeData.message &&
                _.isArray(
                    this.props.main.validateConfirmationCodeData.message
                ) &&
                this.props.main.validateConfirmationCodeData.message[0] &&
                _.isObject(
                    this.props.main.validateConfirmationCodeData.message[0]
                )
            ) {
                errorText =
                    this.props.main.validateConfirmationCodeData &&
                    this.props.main.validateConfirmationCodeData.messages &&
                    (
                        this.props.main.validateConfirmationCodeData
                            .message as ObjectLiteral[]
                    )[0][
                        Object.keys(
                            this.props.main.validateConfirmationCodeData
                                .message[0]
                        )[0]
                    ];
            }

            if (
                this.props.main.validateConfirmationCodeData.origin !== "USER"
            ) {
                const extraErrorData = {
                    status_code:
                        this.props.main.validateConfirmationCodeData
                            .status_code,
                    other_validation_data:
                        this.props.main.validateConfirmationCodeData,
                };

                this.props.logError &&
                    this.props.logError(
                        errorText,
                        Sentry.Severity.Fatal,
                        extraErrorData
                    );
            } else if (
                this.props.main.validateConfirmationCodeData.origin === "USER"
            ) {
                errorText =
                    "The code you entered is incorrect. Please try again.";
            }

            this.setState({
                valid: false,
                showValidation: true,
                validationText: errorText,
            });
        }

        if (
            this.props.main.validateConfirmationCodeData &&
            (this.props.main.validateConfirmationCodeData.status_code === 200 ||
                this.props.main.validateConfirmationCodeData.status_code ===
                    409 ||
                this.props.main.validateConfirmationCodeData.status_code ===
                    404) &&
            prevProps.main.validateConfirmationCodeData &&
            prevProps.main.validateConfirmationCodeData.status_code !==
                this.props.main.validateConfirmationCodeData.status_code
        ) {
            const { cookies, values } = this.props;
            let confirmationCodeCookieMaxAge = 1800; // 30 minutes
            let text = "";
            if (values.emailConfirmation) {
                text = `${JSON.stringify({
                    type: "email",
                    data: { email: values.emailConfirmation },
                })}+++`;
            } else {
                text = `${JSON.stringify({
                    type: "phone",
                    data: {
                        phoneNumber: values.phoneConfirmation,
                        extraData: values.phoneConfirmationFormat,
                    },
                })}+++`;
            }

            if (
                this.props.main.validateConfirmationCodeData.data &&
                values.phoneConfirmation
            ) {
                text = `${text}${JSON.stringify({
                    ...this.props.main.validateConfirmationCodeData.data,
                    fromSalesForce: true,
                })}`;
            } else if (
                this.props.main.validateConfirmationCodeData.data &&
                values.emailConfirmation
            ) {
                text = `${text}${JSON.stringify({
                    ...this.props.main.validateConfirmationCodeData.data,
                    fromSalesForce: true,
                })}`;
            }
            const bytes = utf8.encode(text);
            const content = base64.encode(bytes);

            if (
                this.props.saveSession &&
                cookies &&
                this.props.registrationType
            ) {
                cookies.set(
                    "confirmation_" + this.props.registrationType.toLowerCase(),
                    content,
                    { path: "/", maxAge: confirmationCodeCookieMaxAge }
                );
            }

            if (this.props.main.validateConfirmationCodeData.data) {
                const data = this.props.main.validateConfirmationCodeData.data;

                let mobileNumber;
                let firstName;
                let lastName;
                let email;
                let birthdate;
                let dataComplete = false;

                ({ mobileNumber, firstName, lastName, email, birthdate } =
                    data);

                if (this.props.disableSaveAndCreate) {
                    dataComplete = true;
                } else if (
                    mobileNumber &&
                    firstName &&
                    lastName &&
                    email &&
                    birthdate
                ) {
                    dataComplete = true;
                }

                if (dataComplete) {
                    this.props.handleChange("fromSalesForce")(null, true);

                    if (parentDataRequiredToRegister(birthdate)) {
                        this.props.nextStep(12);
                    } else {
                        this.props.nextStep(6);
                    }
                } else {
                    this.props.nextStep();
                }
            } else {
                this.props.nextStep();
            }
        }
    }

    onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const element = event.currentTarget as HTMLInputElement;
        const codeNumber = parseInt(element.getAttribute("data-code") || "");

        const key = event.key;

        if (
            codeNumber &&
            codeNumber > 0 &&
            /^\d+$/.test(key) &&
            element.value === key
        ) {
            if (codeNumber < codeLength && key !== "") {
                if (
                    this.codeRefs[`code${codeNumber + 1}refInput`] &&
                    this.codeRefs[`code${codeNumber + 1}refInput`].current
                ) {
                    this.codeRefs[
                        `code${codeNumber + 1}refInput`
                    ].current.focus();
                }
            }
        }

        if (
            codeNumber &&
            codeNumber > 0 &&
            (key === "Delete" || key === "Backspace")
        ) {
            if (codeNumber > 1) {
                if (
                    this.codeRefs[`code${codeNumber - 1}refInput`] &&
                    this.codeRefs[`code${codeNumber - 1}refInput`].current
                ) {
                    this.codeRefs[
                        `code${codeNumber - 1}refInput`
                    ].current.focus();
                }
            }
        }
    };

    // Code Change Function
    /**
     * onChangeCode() pushes the user to the next code input field when they are manually entering the code.
     * This also updates the state of each code number, and validates the code upon completion through the API.
     */
    onChangeCode = (event: React.ChangeEvent<HTMLInputElement>) => {
        const codeNumber = parseInt(
            event.target.getAttribute("data-code") || ""
        );
        const value = event.target.value;
        const newState: State = Object.assign({}, this.state);

        `code${codeNumber}` as keyof State;
        if (
            codeNumber &&
            codeNumber > 0 &&
            (/^\d+$/.test(value) || value === "")
        ) {
            newState[`code${codeNumber}` as key] = value;
            if (codeNumber && codeNumber > 0 && /^\d+$/.test(value)) {
                if (codeNumber < codeLength && value !== "") {
                    if (
                        this.codeRefs[`code${codeNumber + 1}refInput`] &&
                        this.codeRefs[`code${codeNumber + 1}refInput`].current
                    ) {
                        this.codeRefs[
                            `code${codeNumber + 1}refInput`
                        ].current.focus();
                    }
                }
            }
        }
        this.setState(newState, () => {
            if (codeNumber === codeLength) {
                // same as next button
                const codeToSend =
                    this.state.code1 +
                    this.state.code2 +
                    this.state.code3 +
                    this.state.code4 +
                    this.state.code5 +
                    this.state.code6;

                if (codeToSend.length === codeLength) {
                    const { values } = this.props;
                    const justPhone = values.phoneConfirmation.replace(
                        values.phoneConfirmationFormat.dialCode,
                        ""
                    );
                    const pn = new PhoneNumber(
                        justPhone,
                        values.phoneConfirmationFormat.countryCode
                    );
                    const validPhoneConfirmation = pn.isValid() || false;
                    let confirmationType: ConfirmationType = "phone";
                    let confirmationTypeValue: ConfirmationTypeData = {
                        data: justPhone,
                    };

                    if (values.phoneConfirmation && validPhoneConfirmation) {
                        confirmationTypeValue.extraInfo = {
                            mobile_dialing_code:
                                values.phoneConfirmationFormat.dialCode,
                        };
                    }

                    if (values.emailConfirmation) {
                        confirmationType = "email";
                        confirmationTypeValue = {
                            data: values.emailConfirmation,
                        };
                    }

                    this.props.actions.main.main_validate_confirmation_code({
                        confirmationCode: codeToSend,
                        confirmation_type: confirmationType,
                        confirmation_type_data: confirmationTypeValue,
                    });
                }
            }
        });
    };

    // Code Paste Function
    /**
     * onPaste() allows the user to paste the full code into all of the available fields.
     * This also updates the state of each code number, and validates the code upon completion through the API.
     */
    onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const code = event.clipboardData.getData("text/plain").trim();
        let codeParsed = "";
        let validCode = true;
        for (let i = 0; i < codeLength; i++) {
            if (/^\d+$/.test(code[i])) {
                codeParsed += code[i];
            } else {
                validCode = false;
                break;
            }
        }

        if (validCode) {
            this.setState({
                code1: codeParsed[0],
                code2: codeParsed[1],
                code3: codeParsed[2],
                code4: codeParsed[3],
                code5: codeParsed[4],
                code6: codeParsed[5],
            });

            const { values } = this.props;
            const justPhone = values.phoneConfirmation.replace(
                values.phoneConfirmationFormat.dialCode,
                ""
            );
            const pn = new PhoneNumber(
                justPhone,
                values.phoneConfirmationFormat.countryCode
            );

            const validPhoneConfirmation = pn.isValid() || false;
            let confirmationType: ConfirmationType = "phone";
            let confirmationTypeValue: ConfirmationTypeData = {
                data: justPhone,
            };

            if (values.phoneConfirmation && validPhoneConfirmation) {
                confirmationTypeValue.extraInfo = {
                    mobile_dialing_code:
                        values.phoneConfirmationFormat.dialCode,
                };
            }

            if (values.emailConfirmation) {
                confirmationType = "email";
                confirmationTypeValue = { data: values.emailConfirmation };
            }

            this.props.actions.main.main_validate_confirmation_code({
                confirmationCode: code,
                confirmation_type: confirmationType,
                confirmation_type_data: confirmationTypeValue,
            });
        }
    };

    // Code Focus Function
    /**
     * onFocus() will always select the content of the select on focus
     */

    onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        event.target.select();
    };

    // Save and Continue Function
    /**
     * saveAndContinue() saves the values to the state and goes to the corresponding next step.
     */
    saveAndContinue = (e: any) => {
        e.preventDefault();
        const codeToSend =
            this.state.code1 +
            this.state.code2 +
            this.state.code3 +
            this.state.code4 +
            this.state.code5 +
            this.state.code6;

        const { values } = this.props;
        const justPhone = values.phoneConfirmation.replace(
            values.phoneConfirmationFormat.dialCode,
            ""
        );
        const pn = new PhoneNumber(
            justPhone,
            values.phoneConfirmationFormat.countryCode
        );

        const validPhoneConfirmation = pn.isValid() || false;
        let confirmationType: ConfirmationType = "phone";
        let confirmationTypeValue: ConfirmationTypeData = { data: justPhone };

        if (values.phoneConfirmation && validPhoneConfirmation) {
            confirmationTypeValue.extraInfo = {
                mobile_dialing_code: values.phoneConfirmationFormat.dialCode,
            };
        }

        if (values.emailConfirmation) {
            confirmationType = "email";
            confirmationTypeValue = { data: values.emailConfirmation };
        }

        this.props.actions.main.main_validate_confirmation_code({
            confirmationCode: codeToSend,
            confirmation_type: confirmationType,
            confirmation_type_data: confirmationTypeValue,
        });
    };

    // Go to Email Function
    /**
     * goToEmail() sends the user to the email step if phoneOrEmail() calls for it.
     */
    goToEmail = (e: any) => {
        e.preventDefault();
        // this.props.phoneOrEmail();
        this.props.handleChange("phoneConfirmation")(null, "");
        this.props.handleChange("phoneConfirmationFormat")(null, {});
        this.props.phoneOrEmail && this.props.phoneOrEmail();
    };

    // Go to Phone Function
    /**
     * goToPhone() sends the user to the phone step.
     */
    goToPhone = (e: any) => {
        e.preventDefault();
        this.props.handleChange("emailConfirmation")(null, "");
        this.props.nextStep(1);
    };

    render() {
        const { values } = this.props;
        const codeToSend =
            this.state.code1 +
            this.state.code2 +
            this.state.code3 +
            this.state.code4 +
            this.state.code5 +
            this.state.code6;

        const pn = new PhoneNumber(
            values.phoneConfirmation.replace(
                values.phoneConfirmationFormat.dialCode,
                ""
            ),
            values.phoneConfirmationFormat.countryCode
        );
        const validPhoneConfirmation = pn.isValid() || false;

        return (
            <>
                <div className="col-xs-12 max-w-500 full-width center-content m-b-spacer-3xl">
                    <h2 className="m-t-spacer-md m-b-spacer-none font-size-24 text-center">
                        Confirm your{" "}
                        {validPhoneConfirmation ? "number" : "email"}
                    </h2>
                    <div className="m-b-spacer-2xl ">
                        {values.phoneConfirmation && validPhoneConfirmation ? (
                            <p className="text-dark text-center">
                                <span className="half-here">
                                    We&#39;ve sent a {codeLength}-digit # to:
                                </span>{" "}
                                +{pn.getCountryCode()}{" "}
                                {pn.getNumber("national")}
                            </p>
                        ) : null}
                        {values.emailConfirmation ? (
                            <p className="text-dark text-center">
                                <span className="half-here">
                                    We&#39;ve sent a {codeLength}-digit # to:
                                </span>{" "}
                                {values.emailConfirmation}
                            </p>
                        ) : null}
                    </div>

                    <p className="text-dark text-center">
                        <span className="half-here">
                            Enter the verification code here
                        </span>
                    </p>
                    <Form
                        className={`m-b-spacer-xl digits-form ${
                            this.state.showValidation && !this.state.valid
                                ? "ec-error"
                                : ""
                        }`}
                    >
                        <Form.Group
                            widths="equal"
                            className="flex flex-justify-between"
                        >
                            <Form.Field>
                                <input
                                    autoFocus={true}
                                    onPaste={this.onPaste}
                                    onFocus={this.onFocus}
                                    placeholder=""
                                    name="code1"
                                    className="text-center no-outline p-x-spacer-sm p-b-spacer-sm border-alt-light-grey b-x-width-none b-t-width-none b-b-width-sm text-alt-dark-grey full-width digits-input"
                                    onKeyUp={this.onKeyUp}
                                    onChange={this.onChangeCode}
                                    maxLength={1}
                                    value={this.state.code1}
                                    data-code={1}
                                    ref={this.codeRefs.code1refInput}
                                    autoComplete="one-time-code"
                                    pattern="[0-9]*"
                                />
                            </Form.Field>
                            <Form.Field>
                                <input
                                    placeholder=""
                                    id="nextID"
                                    name="code2"
                                    className="text-center no-outline p-x-spacer-sm p-b-spacer-sm border-alt-light-grey b-x-width-none b-t-width-none b-b-width-sm text-alt-dark-grey full-width digits-input"
                                    onKeyUp={this.onKeyUp}
                                    onChange={this.onChangeCode}
                                    maxLength={1}
                                    value={this.state.code2}
                                    data-code={2}
                                    ref={this.codeRefs.code2refInput}
                                    pattern="[0-9]*"
                                    onPaste={this.onPaste}
                                    onFocus={this.onFocus}
                                />
                            </Form.Field>
                            <Form.Field>
                                <input
                                    placeholder=""
                                    name="code3"
                                    className="text-center no-outline p-x-spacer-sm p-b-spacer-sm border-alt-light-grey b-x-width-none b-t-width-none b-b-width-sm text-alt-dark-grey full-width digits-input"
                                    onChange={this.onChangeCode}
                                    maxLength={1}
                                    value={this.state.code3}
                                    data-code={3}
                                    ref={this.codeRefs.code3refInput}
                                    pattern="[0-9]*"
                                    onPaste={this.onPaste}
                                    onFocus={this.onFocus}
                                    onKeyUp={this.onKeyUp}
                                />
                            </Form.Field>
                            <Form.Field>
                                <input
                                    placeholder=""
                                    name="code4"
                                    className="text-center no-outline p-x-spacer-sm p-b-spacer-sm border-alt-light-grey b-x-width-none b-t-width-none b-b-width-sm text-alt-dark-grey full-width digits-input"
                                    onChange={this.onChangeCode}
                                    maxLength={1}
                                    value={this.state.code4}
                                    ref={this.codeRefs.code4refInput}
                                    data-code={4}
                                    pattern="[0-9]*"
                                    onPaste={this.onPaste}
                                    onFocus={this.onFocus}
                                    onKeyUp={this.onKeyUp}
                                />
                            </Form.Field>
                            <Form.Field>
                                <input
                                    placeholder=""
                                    name="code5"
                                    className="text-center no-outline p-x-spacer-sm p-b-spacer-sm border-alt-light-grey b-x-width-none b-t-width-none b-b-width-sm text-alt-dark-grey full-width digits-input"
                                    onChange={this.onChangeCode}
                                    maxLength={1}
                                    value={this.state.code5}
                                    data-code={5}
                                    ref={this.codeRefs.code5refInput}
                                    pattern="[0-9]*"
                                    onPaste={this.onPaste}
                                    onFocus={this.onFocus}
                                    onKeyUp={this.onKeyUp}
                                />
                            </Form.Field>
                            <Form.Field>
                                <input
                                    placeholder=""
                                    name="code6"
                                    className="text-center no-outline p-x-spacer-sm p-b-spacer-sm border-alt-light-grey b-x-width-none b-t-width-none b-b-width-sm text-alt-dark-grey full-width digits-input"
                                    onChange={this.onChangeCode}
                                    maxLength={1}
                                    value={this.state.code6}
                                    data-code={6}
                                    ref={this.codeRefs.code6refInput}
                                    pattern="[0-9]*"
                                    onPaste={this.onPaste}
                                    onFocus={this.onFocus}
                                    onKeyUp={this.onKeyUp}
                                />
                            </Form.Field>
                        </Form.Group>
                        {this.state.showValidation && !this.state.valid ? (
                            <small className="p-x-spacer-md ec-primary-font ec-line-height">
                                {this.state.validationText}
                            </small>
                        ) : null}

                        {this.props.isLoading ? (
                            <div className="form-loading rel-pos  m-t-spacer-md  way-forward full-width uptop">
                                <div className="la-ball-clip-rotate la-color la-md loading-spin abs-pos absolute-center">
                                    <div />
                                </div>
                            </div>
                        ) : (
                            <Button
                                id={`${this.props.tagPrefixId}-verify-next`}
                                className="m-t-spacer-lg full-width no-border text-white  p-y-spacer-md uppercase let-sp-reg hover-grow ec-border-radius"
                                disabled={
                                    this.props.isLoading ||
                                    codeToSend.length < codeLength
                                }
                                onClick={this.saveAndContinue}
                            >
                                Next
                            </Button>
                        )}
                    </Form>

                    <div className="m-t-spacer-lg text-center">
                        <small className="regular">
                            <span className="text-alt-dark-grey half-here">
                                Didn&#39;t get a code?
                            </span>
                            <br />
                            <br />
                            {validPhoneConfirmation ? (
                                <Fragment>
                                    <Button
                                        id={`${this.props.tagPrefixId}-verify-resend-sms`}
                                        onClick={this.goToPhone}
                                        className="ec-no-background no-border bold help-text hover-grow ec-border-radius"
                                    >
                                        Resend text
                                    </Button>{" "}
                                    <span className="text-alt-dark-grey half-here">
                                        or{" "}
                                    </span>{" "}
                                    <Button
                                        id={`${this.props.tagPrefixId}-verify-send-email`}
                                        onClick={this.goToEmail}
                                        className="ec-no-background no-border bold help-text hover-grow ec-border-radius"
                                    >
                                        Send email
                                    </Button>
                                </Fragment>
                            ) : (
                                <Fragment>
                                    <Button
                                        id={`${this.props.tagPrefixId}-verify-resend-email`}
                                        onClick={this.goToEmail}
                                        className="ec-no-background no-border bold help-text hover-grow ec-border-radius"
                                    >
                                        Resend email
                                    </Button>
                                    {/* <span className="text-alt-dark-grey half-here hover-grow">
                                        or{" "}
                                    </span>{" "}
                                    <Button
                                        id={`${this.props.tagPrefixId}-verify-send-sms`}
                                        onClick={this.goToPhone}
                                        className="ec-no-background no-border bold help-text ec-border-radius"
                                    >
                                        Send a text message
                                    </Button> */}
                                </Fragment>
                            )}
                        </small>
                    </div>
                </div>
                <ResetForm
                    idPrefix={`${this.props.tagPrefixId}-verify`}
                    cancelUrl={this.props.cancelUrl || ""}
                />
            </>
        );
    }
}

export default withCookies(Code);
